/** Random IDs for X-Request-Id, Idempotency-Key and W3C traceparent.
 *
 * crypto.randomUUID only exists in secure contexts (https or localhost). A console opened
 * over plain http on a LAN address (an offline stage laptop) has getRandomValues but not
 * randomUUID, so v4 is built by hand there rather than failing every request. */

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

const hex = (bytes: Uint8Array) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

export function uuid(): string {
  if (typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch {
      /* insecure context: fall through */
    }
  }
  const b = randomBytes(16);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = hex(b);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** W3C trace-context header: version 00, random trace and span ids, sampled. */
export function traceparent(): string {
  return `00-${hex(randomBytes(16))}-${hex(randomBytes(8))}-01`;
}

/** Key-order-independent JSON, so `{a,b}` and `{b,a}` fingerprint the same. */
export function stableStringify(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`;
}
